import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PromptPowerUp } from '../../components/PromptPowerUp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Music, Download, Save, RefreshCw, Scissors } from 'lucide-react';
import { API, GenerationProgress, downloadFile } from './shared';
import { useIdempotencyKey } from '../../lib/useIdempotencyKey';
import { useStudioErrorHandler } from '../../components/SubmissionUncertainModal';
import { useHubPricing, formatCreditRange } from '../../lib/useHubPricing';
import { useStudioHealth, StudioHealthBanner } from '../../lib/useStudioHealth';

const GENRES = ['pop', 'rock', 'electronic', 'hip-hop', 'jazz', 'classical', 'ambient', 'cinematic', 'lo-fi', 'r&b', 'country', 'latin'];
const STYLE_CHIPS = ['Pop', 'Rock', 'Hip Hop', 'R&B', 'Electronic', 'Jazz', 'Classical', 'Country', 'Latin', 'Indie', 'Metal', 'Folk', 'Reggae', 'Blues', 'Soul', 'Funk', 'Ambient', 'Cinematic', 'Lo-Fi'];

export function MusicStudio({ onSave }) {
  const [mode, setMode] = useState('simple');
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [lyricsMode, setLyricsMode] = useState('auto');
  const [customLyrics, setCustomLyrics] = useState('');
  const [instrumental, setInstrumental] = useState(false);
  const [genre, setGenre] = useState('pop');
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [vocalType, setVocalType] = useState('female');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);

  const toggleStyle = (s) => {
    setSelectedStyles(prev => prev.includes(s) ? prev.filter(x => x !== s) : prev.length < 3 ? [...prev, s] : prev);
  };

  // v3.0 Kaharian contract: stable Idempotency-Key keyed to user's intent.
  // Same inputs → same key → Hub returns original task on retry / StrictMode double-render.
  const idempotencyKey = useIdempotencyKey('konnect-music', [
    mode, prompt, title, customLyrics, lyricsMode, instrumental, genre,
    selectedStyles.join(','), vocalType,
  ]);
  const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
  const { getPrice, isLoading: pricingLoading } = useHubPricing();
  const musicRow = getPrice('MUSIC.song');

  // v3.2: shared provider-health probe + auto-retry
  const { health, cooldown, onProvidersUnavailable, pendingRetryRef } = useStudioHealth(
    'MUSIC',
    { onRecover: () => generate() }
  );

  const generate = async () => {
    if (mode === 'simple' && !prompt.trim()) { toast.error('Describe your song'); return; }
    if (mode === 'custom' && !prompt.trim() && !customLyrics.trim()) { toast.error('Enter a description or lyrics'); return; }
    setGenerating(true); setResult(null); setProgress(0);
    setProgressMsg(instrumental ? 'Composing instrumental...' : 'Composing your song...');
    const progressInterval = setInterval(() => {
      setProgress(p => p >= 85 ? 85 : p + Math.random() * 4);
      const msgs = instrumental
        ? ['Building melody...', 'Layering instruments...', 'Mixing tracks...', 'Mastering audio...']
        : ['Writing melody...', 'Generating vocals...', 'Mixing tracks...', 'Harmonizing...', 'Mastering...'];
      setProgressMsg(msgs[Math.floor(Math.random() * msgs.length)]);
    }, 2500);
    try {
      // If user explicitly picked "No Lyrics" in custom mode, treat as instrumental
      // so the backend never auto-generates lyrics.
      const effectiveInstrumental = instrumental || (mode === 'custom' && lyricsMode === 'none');
      // Build style list: multi-select chips in simple mode, single genre in custom mode.
      const stylesPayload = mode === 'simple' && selectedStyles.length > 0
        ? selectedStyles.map(s => s.toLowerCase())
        : [genre.toLowerCase()];
      const res = await axios.post(`${API}/api/creative-studio/generate/music`, {
        prompt: prompt.trim(),
        title: title.trim(),
        instrumental: effectiveInstrumental,
        genre: genre.toLowerCase(),
        styles: stylesPayload,
        // v3.0 MUSIC contract: never send description as lyrics. Only send user-typed lyrics.
        custom_lyrics: mode === 'custom' && lyricsMode === 'custom' ? customLyrics : '',
        vocal_type: vocalType,
        idempotency_key: idempotencyKey,
      });
      const data = res.data;

      // If async (task_id returned), poll for completion
      if (data.task_id && data.status === 'processing') {
        setProgressMsg('Your song is being composed (30-120 seconds)...');
        setProgress(30);
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes at 5s intervals
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const poll = await axios.post(`${API}/api/check-task-status`, { taskId: data.task_id });
            const pd = poll.data;
            const pct = Math.min(30 + (attempts / maxAttempts) * 65, 95);
            setProgress(pct);
            const pollMsgs = [
              'Composing your song...', 'Generating melodies...', 
              'Laying down the beat...', 'Mixing and mastering...', 
              'Adding final polish...', 'Almost there, hang tight...',
              'Music generation takes 30-120 seconds...',
            ];
            setProgressMsg(pollMsgs[Math.floor(attempts / 4) % pollMsgs.length]);

            // ADDENDUM v3.2.2-r1: canonical read — data.url + data.status === "ready"/"completed".
            // Music may also return a tracks[] array; track[0].audioUrl is still supported.
            const tracks = pd.tracks || [];
            const audioUrl = pd.url || (tracks[0]?.audioUrl) || '';
            const isComplete = pd.status === 'completed' || pd.status === 'ready' || !!audioUrl;

            if (isComplete && (audioUrl || tracks.length > 0)) {
              clearInterval(pollInterval); clearInterval(progressInterval);
              setProgress(100); setProgressMsg('Music ready!');
              setTimeout(() => {
                setResult({ ...data, url: audioUrl, status: 'completed',
                  tracks: tracks.length > 0 ? tracks : (audioUrl ? [{ audioUrl, title: data.title || '' }] : []),
                  lyrics: pd.lyrics || tracks[0]?.lyrics || data.lyrics || '' });
                setGenerating(false);
              }, 500);
            } else if (pd.status === 'failed' || pd.status === 'error') {
              clearInterval(pollInterval); clearInterval(progressInterval);
              toast.error(pd.error || 'Music generation failed. Please try again.');
              setGenerating(false);
            } else if (attempts >= maxAttempts) {
              clearInterval(pollInterval); clearInterval(progressInterval);
              toast.info('Generation is taking longer than expected. Your song is still being created — please check back in a few minutes.');
              setGenerating(false);
            }
          } catch {
            if (attempts >= maxAttempts) {
              clearInterval(pollInterval); clearInterval(progressInterval);
              toast.error('Lost connection to music service.');
              setGenerating(false);
            }
          }
        }, 5000); // Poll every 5s per Kaharian spec
      } else if (data.url) {
        // Immediate result
        clearInterval(progressInterval); setProgress(100); setProgressMsg('Music ready!');
        setTimeout(() => { setResult(data); setGenerating(false); }, 500);
      } else {
        clearInterval(progressInterval);
        toast.error('No audio returned. Credits may have been insufficient.');
        setGenerating(false);
      }
    } catch (err) {
      clearInterval(progressInterval);
      const detail = err?.response?.data?.detail;
      const isProvidersDown = err?.response?.status === 503 && typeof detail?.code === 'string' && detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
      if (isProvidersDown) {
        onProvidersUnavailable(detail, { armRetry: true });
        toast.error(`${detail.message || 'Temporarily unavailable'} · Auto-retrying in 30s…`, { duration: 5000, id: 'music-providers-down' });
      } else {
        handleStudioError(err, { category: 'MUSIC', idempotencyKey, fallbackMessage: 'Music generation failed' });
      }
      setGenerating(false);
    }
  };

  const handleSave = () => {
    const url = result?.url || '';
    onSave({ type: 'music', prompt: result?.prompt || prompt, url, genre,
      lyrics: result?.lyrics || customLyrics, credits_used: result?.credits_used || 0, title: title || prompt || 'AI Music' });
  };

  const audioSrc = result?.url || (result?.tracks?.[0]?.audioUrl) || null;
  const secondTrack = result?.tracks?.[1]?.audioUrl || null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="music-studio">
      <SubmissionUncertainDialog />
      <div className="lg:col-span-5">
        <StudioHealthBanner category="MUSIC" health={health} cooldown={cooldown} pendingRetry={pendingRetryRef.current} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-0 border border-gray-border rounded-xl overflow-hidden" data-testid="music-mode-toggle">
              <button onClick={() => setMode('simple')}
                className={cn("py-3 text-center transition-all", mode === 'simple' ? "bg-emerald-brand/10 border-r border-emerald-brand/30 font-medium" : "bg-white border-r border-gray-border hover:bg-gray-50")}>
                <p className={cn("text-sm font-medium", mode === 'simple' ? "text-emerald-700" : "text-text-primary")}>Simple</p>
                <p className="text-[10px] text-gray-medium mt-0.5">Describe your song</p>
              </button>
              <button onClick={() => setMode('custom')}
                className={cn("py-3 text-center transition-all", mode === 'custom' ? "bg-emerald-brand/10 font-medium" : "bg-white hover:bg-gray-50")}>
                <p className={cn("text-sm font-medium", mode === 'custom' ? "text-emerald-700" : "text-text-primary")}>Custom</p>
                <p className="text-[10px] text-gray-medium mt-0.5">Write lyrics & title</p>
              </button>
            </div>

            {mode === 'simple' ? (
              <>
                <div><Label className="text-xs font-medium">Song Description</Label>
                  <p className="text-[10px] text-gray-medium mt-0.5">Tell us what the song is <em>about</em> — we'll write the lyrics for you.</p>
                  <PromptPowerUp contextType="general">
                  <Textarea placeholder="A dreamy indie folk song about stargazing on a summer night..." value={prompt} onChange={e => setPrompt(e.target.value)} className="mt-1 min-h-[80px] text-sm" data-testid="music-prompt" />
                  </PromptPowerUp></div>
                <div>
                  <Label className="text-xs font-medium">Style Inspiration</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {STYLE_CHIPS.map(s => (
                      <button key={s} onClick={() => toggleStyle(s)}
                        className={cn("px-2.5 py-1 text-xs rounded-full border transition-all",
                          selectedStyles.includes(s) ? "bg-emerald-brand/10 border-emerald-brand text-emerald-700 font-medium" : "border-gray-border text-gray-medium hover:border-gray-400"
                        )} data-testid={`style-${s.toLowerCase().replace(/\s/g,'-')}`}>{s}</button>
                    ))}
                  </div>
                  {selectedStyles.length > 0 && <p className="text-[10px] text-gray-medium mt-1">{selectedStyles.length}/3 styles selected</p>}
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-gray-border bg-gray-50">
                  <div><p className="text-xs font-bold">Instrumental (no vocals)</p></div>
                  <Switch checked={instrumental} onCheckedChange={setInstrumental} data-testid="instrumental-toggle" />
                </div>
                {!instrumental && (
                  <div>
                    <Label className="text-xs font-medium">Vocal Type</Label>
                    <div className="flex gap-1.5 mt-1">
                      {[['male', 'Male'], ['female', 'Female']].map(([k, l]) => (
                        <button key={k} onClick={() => setVocalType(k)}
                          className={cn("px-4 py-1.5 text-xs rounded-lg border transition-all",
                            vocalType === k ? "bg-pink-50 border-pink-400 text-pink-700 font-medium" : "border-gray-border"
                          )} data-testid={`vocal-${k}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div><Label className="text-xs font-medium">Song Title</Label>
                  <Input placeholder="e.g. Sunshine Day" value={title} onChange={e => setTitle(e.target.value)} className="mt-1 text-sm" data-testid="music-title" /></div>
                <div><Label className="text-xs font-medium">Describe Your Song</Label>
                  <p className="text-[10px] text-gray-medium mt-0.5">Topic / mood — used to write lyrics or guide the instrumental, never sung verbatim.</p>
                  <PromptPowerUp contextType="general">
                  <Textarea placeholder="e.g. An upbeat summer pop song with catchy guitar riff..." value={prompt} onChange={e => setPrompt(e.target.value)} className="mt-1 min-h-[80px] text-sm" data-testid="music-prompt-custom" />
                  </PromptPowerUp></div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-gray-border bg-gray-50">
                  <div><p className="text-xs font-bold">Instrumental Only</p><p className="text-[10px] text-gray-medium">No vocals, music only</p></div>
                  <Switch checked={instrumental} onCheckedChange={v => { setInstrumental(v); if (v) setLyricsMode('none'); else setLyricsMode('auto'); }} data-testid="instrumental-toggle-custom" />
                </div>
                {!instrumental && (
                  <div>
                    <Label className="text-xs font-medium">Lyrics</Label>
                    <div className="flex gap-1.5 mt-1">
                      {[['auto', 'Auto-Generate'], ['custom', 'Paste Lyrics'], ['none', 'No Lyrics']].map(([k, l]) => (
                        <button key={k} onClick={() => setLyricsMode(k)} className={cn("px-3 py-1.5 text-xs rounded-lg border transition-all",
                          lyricsMode === k ? "bg-pink-50 border-pink-400 text-pink-700 font-medium" : "border-gray-border")} data-testid={`lyrics-${k}`}>{l}</button>
                      ))}
                    </div>
                    {lyricsMode === 'custom' && (
                      <Textarea placeholder="Paste your song lyrics here..." value={customLyrics} onChange={e => setCustomLyrics(e.target.value)}
                        className="mt-2 min-h-[100px] text-sm" data-testid="custom-lyrics" />
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs">Genre</Label>
                  <Select value={genre} onValueChange={setGenre}>
                    <SelectTrigger className="mt-1 h-8 text-xs capitalize" data-testid="music-genre"><SelectValue /></SelectTrigger>
                    <SelectContent>{GENRES.map(g => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}

            <Button className="w-full h-11" onClick={generate} disabled={generating || pricingLoading || !musicRow || !health.available} data-testid="generate-music-btn">
              <Music className="h-4 w-4 mr-2" />{generating ? 'Generating...' : (!health.available ? `Unavailable${cooldown > 0 ? ` · ${cooldown}s` : ''}` : (!pricingLoading && !musicRow ? 'Unavailable' : `Generate Music — ${pricingLoading ? '…' : formatCreditRange(musicRow)}`))}
            </Button>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-3 relative">
        <GenerationProgress active={generating} progress={Math.round(progress)} message={progressMsg} />
        {audioSrc ? (
          <Card className="bg-white border-gray-border" data-testid="music-result">
            <CardContent className="p-6">
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6 text-center mb-4">
                <Music className="h-14 w-14 mx-auto mb-3 text-pink-500" />
                <p className="text-sm font-medium text-text-primary mb-1 line-clamp-2">{prompt.substring(0, 80)}</p>
                <div className="flex justify-center gap-2">
                  <Badge className="bg-pink-100 text-pink-700 text-xs capitalize">{genre}</Badge>
                  {instrumental && <Badge className="bg-blue-100 text-blue-700 text-xs">Instrumental</Badge>}
                  {result?.tracks?.length > 1 && <Badge className="bg-violet-100 text-violet-700 text-xs">{result.tracks.length} Tracks</Badge>}
                </div>
              </div>

              {/* Track 1 */}
              <div className="mb-2">
                <p className="text-[10px] font-medium text-gray-400 mb-1">Track 1 {result?.tracks?.[0]?.title ? `— ${result.tracks[0].title}` : ''}</p>
                <audio controls src={audioSrc} className="w-full" data-testid="music-track-1" />
              </div>

              {/* Track 2 (v2.7 — Suno returns 2 tracks) */}
              {secondTrack && (
                <div className="mb-3">
                  <p className="text-[10px] font-medium text-gray-400 mb-1">Track 2 {result?.tracks?.[1]?.title ? `— ${result.tracks[1].title}` : ''}</p>
                  <audio controls src={secondTrack} className="w-full" data-testid="music-track-2" />
                </div>
              )}

              {result?.lyrics && (
                <details className="mb-3"><summary className="text-xs cursor-pointer text-gray-medium">View Lyrics</summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">{result.lyrics}</pre>
                </details>
              )}
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={handleSave}><Save className="h-3.5 w-3.5 mr-1" />Save to Library</Button>
                {audioSrc && <Button size="sm" variant="outline" onClick={() => downloadFile(audioSrc, `konnect_music_${Date.now()}.mp3`)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>}
                <Button size="sm" variant="ghost" onClick={() => setResult(null)}><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
              {audioSrc && (
                <Button size="sm" variant="outline" className="w-full mt-2 text-xs" onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/api/creative-studio/music/split-stems`, { audio_url: audioSrc });
                    if (res.data.vocals_url) toast.success('Stems split successfully!');
                    else toast.info('Stem splitting not available for this track');
                  } catch (err) { toast.error(err.response?.data?.detail || 'Stem split failed'); }
                }} data-testid="split-stems-btn"><Scissors className="h-3 w-3 mr-1" />Split Stems (Vocals + Instrumental)</Button>
              )}
            </CardContent>
          </Card>
        ) : !generating ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center">
            <Music className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <p className="text-sm text-gray-medium">Your generated music will appear here</p>
          </CardContent></Card>
        ) : null}
      </div>
    </div>
  );
}
